import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Head from 'next/head';
import H2 from '@base/H2/H2';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Spacings } from '@styles/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const GuideLinesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const GuideLinesContainer = styled.div``;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  padding-top: 100px;
  margin-top: -100px;
`;

const H2Container = styled.div``;

const tableData = [
  {
    label: '1. Guidelines for acts and content that are prohibited',
    id: 'acts'
  },
  { label: '2. Reporting Tools', id: 'reporting_tools' }
];

const GuideLines = () => {
  const router = useRouter();

  const { pathname } = router;
  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>

      <RightContainer>
        <Head>
          <title>Guidelines | Unacademy</title>
        </Head>
        <GuideLinesWrapper>
          <GuideLinesContainer>
            <H2Container>
              <H2 as="h1">User Guidelines</H2>
            </H2Container>
            <br />
            <br />
            <ContentsTable data={tableData} />
            <div>
              <P1>
                We at Unacademy strive to ensure that our Users have a conducive
                learning and teaching environment, which includes interactions
                between the Users to allow the flow of knowledge and exchange of
                ideas.
                <br />
                <br />
                Keeping the interactive nature of the Platform in mind it is
                essential to ensure that the Platform is not misused in anyway
                that would warrant any User having a less than amazing
                experience and following the guidelines we have given below
                would be a great way to do that. You as a User may come cross
                content that you feel is inappropriate, and we have provided
                means by which you can report or flag such content, but do keep
                in mind that the reporting option should be used judiciously and
                if you have any doubts, i.e. if the content is inappropriate
                please go through the guidelines to make an informed decision.
                <br />
                <br />
                Additionally, these guidelines are to be read along with all the
                other policies on the Unacademy platform including but not
                limited to our Terms and Conditions and Privacy Policy.
              </P1>
              <br />
              <br />
              <a name="acts">
                <StyledH3>
                  1. Guidelines for acts and content that are prohibited
                </StyledH3>
              </a>
              <P1>
                <strong>Harmful or dangerous content: </strong>
                We at Unacademy believe the Platform is a safe space for
                learners and educators alike and would like your help in
                ensuring that it remains so. Keeping this in mind any content
                which incites or promotes violence that may cause physical or
                emotional harm or endanger the safety of any individual is
                expressly prohibited on the Platform. Content that requires
                references to harmful or dangerous acts solely for educational
                purposes are allowed. The sale and promotion of any regulated or
                illegal goods is not allowed. The Platform is to be used only
                for the permitted uses as detailed under the Terms and
                Conditions.
              </P1>
              <P1>
                <strong>Hateful content: </strong>
                We realise that there may be instances when there are exchange
                of ideas and opinions which is essential in the learning
                process, while we agree that individuals have the right to voice
                their opinion, we do not encourage or tolerate any hate speech.
                Hate speech is any content where the sole objective is inciting
                hatred against specific individuals or groups with respect to
                but not limited to race or ethnic origin, country caste,
                religion, disability, gender, age, sexual orientation/gender
                identity etc.
              </P1>
              <P1>
                <strong>Violent and graphic content: </strong>
                Unacademy is an educational Platform and the content uploaded is
                restricted to educational content alone, content that is violent
                or graphic is prohibited. You are requested to ensure that if
                there are references being made to violent or graphic situations
                of instances, it should solely be for education purposes.
                Content whose sole objective is to sensationalise, shock or
                disturb individuals is not allowed. Unacademy does not allow any
                content related to terrorism, such as content that promotes
                terrorist acts or incites violence, is not to be uploaded on the
                Platform in any manner.
              </P1>
              <P1>
                <strong>Harassment and bullying: </strong>
                Unacademy Platform is used by many users on a daily basis and it
                is important to be respectful and kind to your fellow users, we
                do not tolerate any form of harassment or bullying on the
                Platform and strive to keep the Platform a safe space to foster
                learning. Harassment in this case would include but not be
                limited to abusive videos, comments, messages, revealing
                someone’s personal information, including sensitive personally
                identifiable information of individuals, content or comments
                uploaded in order to humiliate someone, sexual harassment or
                sexual bullying in any form.
              </P1>
              <P1>
                <strong>Spam: </strong>
                Posting untargeted, unwanted and repetitive content in lessons,
                comments of messages with an intention to spam the Platform and
                to drive traffic from the Platform to other third-party sites is
                in direct violation of our Terms and Conditions. Posting links
                to external websites with malware and other prohibited sites is
                not allowed. The use or launch of any automated system in any
                manner that sends more request messages to Unacademy’s servers
                in a given period of time that is more than a human can
                reasonably produce using a conventional on-line web browser is
                prohibited and you can take a look at our Terms and Conditions
                for more details on the same including exceptions to this
                prohibition to public search engines.
              </P1>
              <P1>
                <strong>Misleading metadata: </strong>
                Misuse of title, description, tags, thumbnail and bios and other
                features which constitutes the Metadata on the Platform is not
                allowed. Using these features to trick or circumvent our search
                algorithms is prohibited
              </P1>
              <P1>
                <strong>Scams: </strong>
                Any content uploaded/posted in order to trick others for their
                own financial gain is not allowed and we at Unacademy do not
                tolerate any practices of extortion or blackmail either.
              </P1>
              <P1>
                <strong>Copyright:</strong>
                Please refer to our copyright policy provided in our Terms and
                Conditions to know more about proprietary information relating
                to User Content and Unacademy Content.
              </P1>
              <P1>
                <strong>Privacy violation: </strong>
                Kindly refer to our Privacy Policy given here to know how to
                protect your privacy and respect the privacy of other users. If
                you believe that your privacy has been violated is any manner
                where a user has knowingly or unknowingly disclosed any
                information on the Platform be sure to reach out to the User and
                if that is not possible, please feel free to approach us at
                anytime so we can take the necessary steps to remove the
                content.
              </P1>
              <P1>
                <strong>Impersonation: </strong>
                Impersonating another person is not permitted while using the
                Unacademy Platform. In this case impersonation would mean the
                intention to cause confusion regarding who the real person is by
                pretending to be them. This can be done by means of using names,
                image, documents, certificates etc. not belonging to you or not
                used to identify you. Pretending to be a company, institute,
                group etc.by using their logo, brand name or any distinguishing
                mark would also amount to impersonation and could also be a
                potential trademark infringement.
              </P1>
              <P1>
                <strong>Interaction with Unacademy: </strong>
                At Unacademy offices we treat each other with respect and have a
                healthy and supportive work environment. We believe in hands on
                interaction with our users to help them navigate and gain the
                maximum benefit from the platform. Should you find yourselves
                interacting with any person from Unacademy please ensure that
                you maintain the same decorum you would while using this
                Platform. We do not encourage any communication with Unacademy
                employee of staff that is hateful, abusive or sexually
                suggestive in any manner.
              </P1>
              <br />
              <br />
              <a name="reporting_tools">
                <StyledH3>2. Reporting tools</StyledH3>
              </a>
              <P1>
                We have reporting tools available on the platform where you can
                report any content that you believe falls under the ambit of any
                of the prohibited acts, violative of any intellectual property
                rights or spam. The report option is made available for the
                videos as well as the comments, please ensure that you are
                certain that there is an issue before you report and do not make
                false complaints or allegations. You can write to us at
                help@unacademy.com for any additional support, but to ensure
                that the Platform remains a safe learning space we suggest you
                use the in-built reporting tools made available.
              </P1>
              <br />
              <P1>
                <strong>
                  Tips on Do’s & Don’ts: Here are a few tips from us on how to
                  use the platform in a safe manner:
                </strong>
              </P1>
              <br />
              <strong>A. Keeping yourself and your account safe</strong>
              <br />
              <P1>
                Keep your log-in credentials a secret and do not reveal your
                password or ID to anyone.
                <br />
                Create a strong password to ensure that your account is not
                compromised, here are some tips to ensure that your password is
                strong:
                <br />
                Your password should be at least eight characters in length,
                combine numbers and letters, and not include commonly used
                words.
                <br />
                Select a word or acronym and insert numbers between some of the
                letters.
                <br />
                Include Special characters.
                <br />
                Mix capital and lowercase letters.
                <br />
                Don&apos;t reuse passwords associated with any other type of
                account.
                <br />
                Log out of all devices to ensure that your log-in credentials
                are secure.
                <br />
                Register with our phone number in order to ensure you have a
                back up in case you forget your login details.
                <br />
                Do not reveal any personal information about yourself to anyone
                online, additionally do not meet someone you have a conversation
                with online in person, this could be a potentially dangerous act
                and you might be compromising your safety.
                <br />
                If you are below the age of 18, and want to use the platform, a
                parent or guardian can create an account on your behalf. Persons
                below the age of 18 are not allowed to have individual account
                without the consent of a parent or guardian.
                <br />
                <br />
              </P1>
              <strong>B. Reporting Content </strong>
              <br />
              <P1>
                If you have content on your profile or video that would confuse
                persons as to who the video or content belongs to or leads users
                to believe that you have been endorsed or sponsored by another
                person, then the trademark of such person might have been
                infringed. If we receive any complaints or reports regarding the
                same, we may remove the content, if supported with relevant
                evidence.
                <br />
                If you are a trademark or copyright owner and believe that your
                intellectual property right is being infringed, please note that
                you have the option of reporting the same. However, Unacademy is
                not at a position where they can mediate such disputes and we
                disclaim any liability for the same. If we receive an
                infringement complaint that is genuine, we may remove the
                content in question and serve a warning to the alleged
                infringer. We also suggest that before reporting the issue, both
                parties try to solve the disputes directly with the user who
                posted the content.
                <br />
                If you see any content that you feel is inappropriate and
                violates the guidelines given here, please report the content.
                <br />
                If you encounter a video that you believe violates the privacy
                of an individual or violates your privacy, please report the
                same, we shall initiate action accordingly as we have a strict
                privacy policy. If you report any content for violation of
                privacy, the content will be removed only if the information,
                images or date is clearly identifiable.
                <br />
                In the event a fellow user harasses you or you believe that
                someone is being harassed on the platform you can report the
                user or content. You may even write to us at help@unacademy.com
                for any further help or assistance.
                <br />
                <br />
              </P1>
              <strong>C. Posting Content </strong>
              <P1>
                When posting videos or lessons on the platform always ensure
                that the information is correct, the content itself does not
                belong to a third party and that it is free from any of the
                prohibited acts in these guidelines.
                <br />
                Always ensure that the personal information of a person or even
                your own personally identifiable information is not disclosed
                while posting any content on the platform, this would be in
                violation of our Privacy Policy.
                <br />
                You will be personally held responsible for your actions on the
                Platform, thus please ensure that any content that you post
                including comments does not hurt the sentiments of any group or
                individual and cannot be construed as being harassment or
                bullying.
                <br />
                <br />
              </P1>
            </div>
          </GuideLinesContainer>
        </GuideLinesWrapper>
      </RightContainer>
    </>
  );
};

GuideLines.pageInfo = {
  name: 'Unacademy Guidelines',
  isMWebSupported: true,
  layoutOptions: {
    hideGoalSelector: true,
    disableDarkMode: true
  }
};

export default GuideLines;
GuideLines.Layout = Layout;
GuideLines.Wrapper = PageReduxProvider;
